// ============================================================
// RPG Progression - Leveling, Stats, Equipment (v2.0)
// ============================================================
import { XP_BASE, XP_EXPONENT, CLASS_BASE_STATS, CLASS_STAT_GROWTH, RESPAWN_TIME_BASE, ARCHER_MAX_ARROWS, ASSASSIN_MAX_ENERGY, } from '../constants/balance.js';
/**
 * XP required to go from level N to N+1.
 */
export function xpForLevel(level) {
    return Math.floor(XP_BASE * Math.pow(level, XP_EXPONENT));
}
/**
 * Add XP to a hero, handling level-ups.
 */
export function addXp(hero, amount) {
    let levelsGained = 0;
    hero.xp += amount;
    while (hero.xp >= hero.xpToNextLevel) {
        hero.xp -= hero.xpToNextLevel;
        hero.level += 1;
        hero.xpToNextLevel = xpForLevel(hero.level + 1);
        levelsGained++;
        // Apply stat growth — use heroKey for hero-specific growth tables (e.g. 'dwarf' for Lootfi)
        const growthKey = (hero.heroKey ?? hero.heroClass);
        const growth = CLASS_STAT_GROWTH[growthKey] ?? CLASS_STAT_GROWTH[hero.heroClass];
        hero.stats.maxHp += growth.hp;
        hero.stats.hp = hero.stats.maxHp; // Full HP on level up
        hero.stats.maxMp += growth.mp;
        hero.stats.mp = hero.stats.maxMp; // Full mana on level up
        hero.stats.attack += growth.attack;
        hero.stats.defense += growth.defense;
        hero.stats.speed += growth.speed;
        hero.stats.critChance += growth.critChance;
        hero.stats.critDamage += growth.critDamage;
        hero.stats.spellPower += growth.spellPower;
        // Restore class-specific resources on level up
        if (hero.heroClass === 'warrior')
            hero.stats.rage = hero.stats.maxRage;
        if (hero.heroClass === 'assassin')
            hero.stats.energy = hero.stats.maxEnergy;
    }
    return { hero, levelsGained };
}
/**
 * Instantly advance a hero to the target level, applying stat growth for each step.
 * Used when recruiting a new hero mid-run so they aren't one-shot by enemies.
 * XP is reset to 0 at the new level.
 */
export function levelHeroTo(hero, targetLevel) {
    const growthKey = (hero.heroKey ?? hero.heroClass);
    const growth = CLASS_STAT_GROWTH[growthKey] ?? CLASS_STAT_GROWTH[hero.heroClass];
    while (hero.level < targetLevel) {
        hero.level += 1;
        hero.stats.maxHp += growth.hp;
        hero.stats.maxMp += growth.mp;
        hero.stats.attack += growth.attack;
        hero.stats.defense += growth.defense;
        hero.stats.speed += growth.speed;
        hero.stats.critChance += growth.critChance;
        hero.stats.critDamage += growth.critDamage;
        hero.stats.spellPower += growth.spellPower;
    }
    hero.stats.hp = hero.stats.maxHp;
    hero.stats.mp = hero.stats.maxMp;
    if (hero.heroClass === 'warrior')
        hero.stats.rage = hero.stats.maxRage;
    if (hero.heroClass === 'assassin')
        hero.stats.energy = hero.stats.maxEnergy;
    hero.xp = 0;
    hero.xpToNextLevel = xpForLevel(hero.level + 1);
}
/**
 * Create a fresh level 1 hero.
 */
export function createHero(id, name, heroClass, heroKey) {
    const baseKey = (heroKey ?? heroClass);
    const base = (CLASS_BASE_STATS[baseKey] ?? CLASS_BASE_STATS[heroClass]);
    const stats = {
        ...base,
        spellPower: base.spellPower,
        rage: 0,
        maxRage: heroClass === 'warrior' ? 100 : 0,
        energy: heroClass === 'assassin' ? ASSASSIN_MAX_ENERGY : 0,
        maxEnergy: heroClass === 'assassin' ? ASSASSIN_MAX_ENERGY : 0,
        arrowCount: heroClass === 'archer' ? ARCHER_MAX_ARROWS : 0,
        maxArrows: heroClass === 'archer' ? ARCHER_MAX_ARROWS : 0,
        reloadTimer: 0,
        fireResist: 0,
        frostResist: 0,
        shadowResist: 0,
        natureResist: 0,
    };
    const hero = {
        id,
        name,
        heroClass,
        ...(heroKey ? { heroKey } : {}),
        level: 1,
        xp: 0,
        xpToNextLevel: xpForLevel(2),
        stats,
        baseStats: { ...stats },
        abilities: getStartingAbilities(heroKey ?? heroClass),
        equipment: {
            weapon1: null,
            weapon2: null,
            armor: null,
            reserved: null,
            relic1: null,
            relic2: null,
            relic3: null,
            relic4: null,
        },
        isDead: false,
        respawnTimer: 0,
        totalKills: 0,
        respawnUsesLeft: 0,
        permanentlyDead: false,
        talentBonuses: {},
    };
    // Sylvari's archer pet removed — rangers no longer start with a companion.
    return hero;
}
/**
 * Get respawn time for a hero in ms.
 */
/** Respawn time is flat 3 seconds for all heroes (1 free use per game per hero). */
export function getRespawnTime(_heroLevel) {
    return RESPAWN_TIME_BASE;
}
/**
 * Starting abilities per class (v2.0 overhaul).
 */
export function getHeroAbilities(heroKey) {
    return getStartingAbilities(heroKey);
}
function getStartingAbilities(heroKey) {
    const abilities = {
        // Warrior: rage-based, melee
        warrior: [
            {
                id: 'whirlwind', name: 'Whirlwind',
                description: 'Strike up to 4 enemies for 36 damage + bleed for 10% of damage over 10s. Empowered by ATK.',
                manaCost: 30, cooldown: 18000, currentCooldown: 0,
                damage: 36, healAmount: 0, effectType: 'dot',
                dotFraction: 0.10, effectDuration: 10000, targetCount: 4,
                iconKey: 'spell_whirlwind', level: 1, maxLevel: 10,
            },
            {
                id: 'earthquake', name: 'Earthquake',
                description: 'Slam the ground — stuns the target and all enemies behind it in a cone for 2s; while stunned they take 25% bonus damage from spells',
                manaCost: 40, cooldown: 40000, currentCooldown: 0,
                damage: 0, healAmount: 0, effectType: 'stun_cone',
                effectDuration: 2000, bonusDamageMult: 1.25,
                iconKey: 'spell_earthquake', level: 1, maxLevel: 10,
            },
            {
                id: 'berserk', name: 'Berserk',
                description: 'Unleash savage fury — grow in size, strike with brutal force, and tear through enemies at blistering speed for 8s',
                manaCost: 30, cooldown: 24000, currentCooldown: 0,
                damage: 0, healAmount: 0, effectType: 'berserk',
                effectDuration: 8000,
                iconKey: 'spell_berserk', level: 1, maxLevel: 10,
            },
            {
                id: 'undying_fury', name: 'Undying Fury',
                description: 'Upon death, Goomar resurrects with 30% HP (+5% per talent rank), 2× attack power and 1.5× size for 8s. 210s cooldown.',
                manaCost: 0, cooldown: 210000, currentCooldown: 0,
                damage: 0, healAmount: 0, effectType: 'passive_resurrect',
                effectDuration: 210000,
                iconKey: 'spell_undying_fury', level: 1, maxLevel: 7,
            },
        ],
        // Paladin (tank): challenge, resurrect, consecration, divine reflect
        tank: [
            {
                id: 'challenge', name: 'Challenge',
                description: 'Roar a challenge at the closest enemy within range, forcing it to attack Ironheld for 4s. Grants Ironheld 15% all resist for 4s. Ironheld holds his ground and does not advance.',
                manaCost: 30, cooldown: 12000, currentCooldown: 0,
                damage: 0, healAmount: 0, effectType: 'taunt',
                effectDuration: 4000,
                iconKey: 'spell_challenge', level: 1, maxLevel: 10,
            },
            {
                id: 'shield', name: 'Holy Shield',
                description: 'Shield a damaged ally absorbing 100 HP (+3 per spell power)',
                manaCost: 15, cooldown: 21000, currentCooldown: 0,
                damage: 0, healAmount: 180, effectType: 'buff',
                iconKey: 'spell_shield', level: 1, maxLevel: 10,
            },
            {
                id: 'consecration', name: 'Consecration',
                description: 'Hallowed light scours every enemy on the field. +30 % damage per talent rank.',
                manaCost: 25, cooldown: 12000, currentCooldown: 0,
                damage: 45, healAmount: 0, effectType: 'aoe',
                targetCount: 99,
                iconKey: 'spell_consecration', level: 1, maxLevel: 10,
            },
            {
                id: 'divine_reflect', name: 'Divine Reflect',
                description: 'Reflect 30% of enemy attacks back for 4s (max 50% at rank 4)',
                manaCost: 30, cooldown: 25000, currentCooldown: 0,
                damage: 0, healAmount: 0, effectType: 'reflect',
                effectDuration: 4000,
                iconKey: 'spell_divine_reflect', level: 1, maxLevel: 4,
            },
        ],
        // Wizard: spell power based, fireball + eruption + arcane_might + magma_shield
        wizard: [
            {
                id: 'fireball', name: 'Fireball',
                description: 'Hurl a ball of flame that explodes on impact for 80 damage, leaving the enemy scorched and burning over time.',
                manaCost: 6, cooldown: 10000, currentCooldown: 0,
                damage: 80, healAmount: 0, effectType: 'fireball',
                dotFraction: 0.10, effectDuration: 4000,
                iconKey: 'spell_fireball', level: 1, maxLevel: 10,
            },
            {
                id: 'eruption', name: 'Eruption',
                description: 'Call forth a volcanic eruption — every enemy on the field takes 58 damage (plus your Spell Power) and continues to burn over time. Spreads thin in dense crowds: 80% damage vs 6+ enemies, 50% vs 10+.',
                manaCost: 18, cooldown: 18000, currentCooldown: 0,
                damage: 58, healAmount: 0, effectType: 'eruption',
                dotFraction: 0.10, effectDuration: 3000,
                iconKey: 'spell_eruption', level: 1, maxLevel: 10,
            },
            {
                id: 'arcane_might', name: 'Arcane Might',
                description: 'Grant mana regen buff and +1% resist all to all allies for 10s',
                manaCost: 50, cooldown: 60000, currentCooldown: 0,
                damage: 0, healAmount: 0, effectType: 'buff',
                effectDuration: 10000,
                iconKey: 'spell_arcane_might', level: 1, maxLevel: 5,
            },
            {
                id: 'magma_shield', name: 'Magma Shield',
                description: 'Magma barrier: +100% DEF for 4s.\nEach enemy that strikes Zyphara is stunned for 0.5s.',
                manaCost: 20, cooldown: 18000, currentCooldown: 0,
                damage: 0, healAmount: 0, effectType: 'magma_shield',
                effectDuration: 4000,
                iconKey: 'spell_magma_shield', level: 1, maxLevel: 10,
            },
        ],
        // Ranger: bow, multishot, trap, piercing shot, eagle eye
        archer: [
            {
                id: 'multishot', name: 'Multishot',
                description: 'Fire arrows hitting up to 4 enemies. Talent ranks 1-3 add extra targets; ranks 4-5 add +20% damage per rank.',
                manaCost: 20, cooldown: 10000, currentCooldown: 0,
                damage: 35, healAmount: 0, effectType: 'aoe',
                targetCount: 4,
                iconKey: 'spell_multishot', level: 1, maxLevel: 10,
            },
            {
                id: 'vine_snare', name: 'Poison Trap',
                description: 'Plants a venomous trap that seizes the 3 frontmost enemies. Roots them for 2s and inflicts severe poison damage over time.',
                manaCost: 25, cooldown: 14000, currentCooldown: 0,
                damage: 55, healAmount: 0, effectType: 'vine_snare',
                dotFraction: 0.9, effectDuration: 6000,
                iconKey: 'spell_poison_trap', level: 1, maxLevel: 10,
            },
            {
                id: 'piercing_shot', name: 'Piercing Shot',
                description: 'A devastating shot fired with lethal precision — obliterates a single target, ignoring 50% of its armor. Each rank shreds an additional 10% armor for 3s (up to 70%). Long cooldown, massive impact.',
                manaCost: 25, cooldown: 40000, currentCooldown: 0,
                damage: 180, healAmount: 0, effectType: 'damage',
                iconKey: 'spell_piercing_shot', level: 1, maxLevel: 10,
            },
            {
                id: 'eagle_eye', name: 'Eagle Eye',
                description: 'Sharpen focus: +15% crit chance for 8s (self only)',
                manaCost: 20, cooldown: 20000, currentCooldown: 0,
                damage: 0, healAmount: 0, effectType: 'buff',
                effectDuration: 8000,
                iconKey: 'spell_eagle_eye', level: 1, maxLevel: 10,
            },
        ],
        // Priest: fear, word of death, shield, bless
        healer: [
            {
                id: 'fear', name: 'Fear',
                description: 'Fear the closest enemy — they flee backwards and take +5% spell damage. Has no effect on already-feared targets.',
                manaCost: 25, cooldown: 60000, currentCooldown: 0,
                damage: 0, healAmount: 0, effectType: 'fear',
                bonusDamageMult: 0.05, effectDuration: 3000,
                iconKey: 'spell_fear', level: 1, maxLevel: 10,
            },
            {
                id: 'word_of_death', name: 'Word of Death',
                description: 'Lay a dark curse on the target: 50 instant damage plus 12 damage every second until it fades.',
                manaCost: 12, cooldown: 8000, currentCooldown: 0,
                damage: 50, healAmount: 0, effectType: 'dot',
                dotFraction: 0.25, effectDuration: 10000,
                iconKey: 'spell_word_of_death', level: 1, maxLevel: 10,
            },
            {
                id: 'resurrection', name: 'Resurrection',
                description: 'Revive a fallen ally with 50% HP',
                manaCost: 100, cooldown: 180000, currentCooldown: 0,
                damage: 0, healAmount: 0, effectType: 'resurrect',
                iconKey: 'spell_resurrection', level: 1, maxLevel: 7,
            },
            {
                id: 'bless', name: 'Bless',
                description: 'Heal over time on most injured ally: 200 HP (+2 per SP) over 10s. Talent rank 1: heals 2 heroes. Rank 2: heals all 3 heroes',
                manaCost: 18, cooldown: 15000, currentCooldown: 0,
                damage: 0, healAmount: 200, effectType: 'hot',
                effectDuration: 10000,
                iconKey: 'spell_bless', level: 1, maxLevel: 3,
            },
        ],
        // Assassin: energy-based, poison, shuriken, evasion
        assassin: [
            {
                id: 'sinister_strike', name: 'Sinister Strike',
                // Cooldown bumped 1 s → 2 s per user balance pass — the 1 s spam
                // was making it the only ability Kael cared about.
                description: 'Vicious strike dealing magic damage over 10s',
                manaCost: 5, cooldown: 2000, currentCooldown: 0,
                damage: 15, healAmount: 0, effectType: 'dot',
                effectDuration: 10000,
                iconKey: 'spell_sinister_strike', level: 1, maxLevel: 10,
            },
            {
                id: 'wraith_strike', name: 'Dirty Blades',
                // Cooldown 20 s → 24 s and now grants invulnerability for the
                // teleport window (handled in BattleScene.applyDamageToHero via
                // the `_wraithStriking` flag). The previous version left Kael
                // exposed at his spawn point during the 750 ms vanish, which
                // made the burst self-defeating into AoE waves.
                description: 'Vanishes into shadow and strikes from behind, untouchable while it lasts (+25% dmg per rank, max +200% at rank 8).',
                manaCost: 35, cooldown: 24000, currentCooldown: 0,
                damage: 80, healAmount: 0, effectType: 'shadow_step',
                iconKey: 'spell_poison_cloud', level: 1, maxLevel: 10,
            },
            {
                id: 'shuriken_barrage', name: 'Shuriken Barrage',
                description: 'Throw massive amount of shurikens hitting up to 6 enemies',
                manaCost: 45, cooldown: 12000, currentCooldown: 0,
                damage: 100, healAmount: 0, effectType: 'aoe',
                targetCount: 6,
                iconKey: 'spell_shuriken_barrage', level: 1, maxLevel: 10,
            },
            {
                id: 'evasion', name: 'Evasion',
                description: 'Dodge 60% of attacks for 4s',
                manaCost: 30, cooldown: 22000, currentCooldown: 0,
                damage: 0, healAmount: 0, effectType: 'evasion',
                effectDuration: 4000,
                iconKey: 'spell_evasion', level: 1, maxLevel: 10,
            },
        ],
        // Dwarf (Lootfi): rage-based, tough melee bruiser
        dwarf: [
            {
                id: 'lucky_loot', name: 'Lucky Loot',
                description: '+10% gold and item drop chance while Lootfi is deployed.',
                manaCost: 0, cooldown: 0, currentCooldown: 0,
                damage: 0, healAmount: 0, effectType: 'passive_loot',
                iconKey: 'spell_lucky_loot', level: 1, maxLevel: 1,
            },
            {
                id: 'stone_form', name: 'Stone Form',
                description: 'Transform into living stone: +150% defense for 6s, immune to knockback.',
                manaCost: 30, cooldown: 22000, currentCooldown: 0,
                damage: 0, healAmount: 0, effectType: 'stone_form',
                effectDuration: 6000,
                iconKey: 'spell_stone_form', level: 1, maxLevel: 10,
            },
            {
                id: 'thunder_strike', name: 'Thunder Strike',
                description: 'Call down a lightning bolt on one enemy — always a critical hit.',
                manaCost: 40, cooldown: 14000, currentCooldown: 0,
                damage: 160, healAmount: 0, effectType: 'thunder_strike',
                iconKey: 'spell_thunder_strike', level: 1, maxLevel: 10,
            },
            {
                id: 'mace_throw', name: 'Mace Throw',
                description: 'Hurl the mace at an enemy — deals more damage the farther the target. Dazes on hit for 2s.',
                manaCost: 10, cooldown: 16000, currentCooldown: 0,
                damage: 55, healAmount: 0, effectType: 'mace_throw',
                iconKey: 'spell_mace_throw', level: 1, maxLevel: 10,
            },
        ],
        // Amazon (Zayla): mana-based, melee/ranged hybrid — jump strike, freeze, war cry, passive javelin
        amazon: [
            {
                id: 'leap_strike', name: 'Leap Strike',
                description: 'Launch into the air and crash down on the target — ignores most armor. Requires front-row positioning.',
                manaCost: 40, cooldown: 56000, currentCooldown: 0,
                damage: 120, healAmount: 0, effectType: 'jump_strike',
                iconKey: 'spell_leap_strike', level: 1, maxLevel: 10,
            },
            {
                id: 'freezing_shot', name: 'Freezing Shot',
                description: 'A frost-tipped arrow that locks the target in ice — unable to move or attack.',
                manaCost: 30, cooldown: 22000, currentCooldown: 0,
                damage: 15, healAmount: 0, effectType: 'freeze',
                effectDuration: 3000,
                iconKey: 'spell_freezing_shot', level: 1, maxLevel: 10,
            },
            {
                id: 'war_cry', name: 'War Cry',
                description: 'A battle cry that surges all allies with fury — increasing their attack speed. Ranks add +10% speed & +2s duration.',
                manaCost: 25, cooldown: 20000, currentCooldown: 0,
                damage: 0, healAmount: 0, effectType: 'war_cry',
                effectDuration: 8000, attackSpeedBuff: 0.10,
                iconKey: 'spell_war_cry', level: 1, maxLevel: 3,
            },
            {
                id: 'spear_volley', name: 'Spear Volley',
                description: 'Every tenth strike hurls a massive javelin dealing devastating damage. Ignores armor.',
                manaCost: 0, cooldown: 0, currentCooldown: 0,
                damage: 0, healAmount: 0, effectType: 'passive_spear',
                iconKey: 'spell_spear_volley', level: 1, maxLevel: 1,
            },
        ],
        // Valkyrie (Valka) — rage-based barbarian: frontline DPS with shield tools.
        //   Shield Bash Charge   — forward charge that slows + grants absorb shield.
        //   Tempest Slam         — AoE spin; a front-cone slam finisher scales with
        //                          the number of unique enemies struck during the spin.
        //   Banner of Valor      — plants a persistent buff aura at her feet.
        //   Counter-Strike       — passive: % chance to retaliate on melee hits.
        valkyrie: [
            {
                id: 'shield_bash_charge', name: 'Shield Bash',
                description: 'Charge forward — first enemy hit takes damage + 40% slow for 2s. Valka gains an absorb shield worth 25% of her max HP for 4s.',
                manaCost: 30, cooldown: 20000, currentCooldown: 0,
                damage: 126, healAmount: 0, effectType: 'shield_bash_charge',
                effectDuration: 4000,
                iconKey: 'spell_valka_shield_bash', level: 1, maxLevel: 10,
            },
            {
                id: 'whirlwind_bludgeon', name: 'Tempest Slam',
                description: 'Spin for 1.2s striking nearby enemies in 3 ticks (~50% ATK each). Finishes with a Tempest Slam in a front cone: 162% ATK + 36% per unique enemy struck during the spin.',
                manaCost: 20, cooldown: 16000, currentCooldown: 0,
                damage: 50, healAmount: 0, effectType: 'whirlwind_bludgeon',
                effectDuration: 1500,
                iconKey: 'spell_valka_whirlwind', level: 1, maxLevel: 10,
            },
            {
                id: 'plant_banner', name: 'Banner of Valor',
                // Matches the actual implementation in BattleScene's plant_banner
                // handler: stamps `_warCryBuff = 0.15` on every ally within ~160 px,
                // which speeds up their attacks. Old description (+15% damage / +10
                // defence) was a stale spec — code never granted those.
                description: 'Plant a rallying banner at your feet. Allies within range gain +15% attack speed for 10s. Banner cannot be destroyed.',
                manaCost: 45, cooldown: 22000, currentCooldown: 0,
                damage: 0, healAmount: 0, effectType: 'plant_banner',
                effectDuration: 10000,
                iconKey: 'spell_valka_banner', level: 1, maxLevel: 10,
            },
            {
                id: 'counter_strike', name: 'Counter-Strike',
                description: 'Passive: 30% chance on melee hit taken to retaliate with a 72% ATK basic attack. Rank 1: +15% chance (45% total). Rank 2: also counters ranged attackers. Rank 3-6: +5% chance per rank (max 70% at rank 6).',
                manaCost: 0, cooldown: 0, currentCooldown: 0,
                damage: 0, healAmount: 0, effectType: 'passive_counter',
                iconKey: 'spell_valka_counter', level: 1, maxLevel: 1,
            },
        ],
        // Necromancer (Morveth): SP-based, death_coil + raise_dead + soul_leech + bone_shield
        // Druid (Thorne) — hybrid nature caster. Damage via Thornlash + Wild Growth, sustain via Regrowth + Bear Form.
        druid: [
            {
                id: 'thornlash', name: 'Thornlash',
                description: 'Human form: Moonfire-style spell — 40 + 0.5×SP ranged damage to the nearest enemy anywhere on the field. Each cast adds a stack (max 3, 8s) that grants +15 damage on the next hit.\nBear form: the classic Thornlash — 75 + SP damage with a 30% bleed over 8s, +50% damage bonus, melee-range bite.\nTalent scales damage doubling each rank.',
                manaCost: 16, cooldown: 4000, currentCooldown: 0,
                damage: 75, healAmount: 0, effectType: 'dot',
                dotFraction: 0.30, effectDuration: 8000,
                iconKey: 'spell_thornlash', level: 1, maxLevel: 10,
            },
            {
                id: 'regrowth', name: 'Regrowth',
                description: 'Verdant growth heals the most wounded ally for 65 HP + 2×SP over 8s.\nTalent rank 1: heals 2 allies. Rank 2: heals all 3. Rank 3: +25% burst at end. Rank 4: +50% burst at end.',
                manaCost: 22, cooldown: 12000, currentCooldown: 0,
                damage: 0, healAmount: 65, effectType: 'hot',
                effectDuration: 8000,
                iconKey: 'spell_regrowth', level: 1, maxLevel: 4,
            },
            {
                id: 'wild_growth', name: 'Wild Growth',
                description: 'Thorned roots erupt around Thorne — all nearby enemies take 80 + SP damage and are rooted for 2s (cannot move, can still attack).\nTalent scales damage doubling each rank.',
                manaCost: 30, cooldown: 16000, currentCooldown: 0,
                damage: 80, healAmount: 0, effectType: 'aoe',
                targetCount: 20,
                iconKey: 'spell_wild_growth', level: 1, maxLevel: 10,
            },
            {
                id: 'bear_form', name: 'Bear Form',
                description: 'Shift into a great vine-bound bear for 8s: +20% attack, −25% damage taken. Thornlash hits 50% harder but Regrowth and Wild Growth cannot be cast while shifted.\nTalent doubles the ATK bonus and damage reduction each rank.',
                manaCost: 30, cooldown: 60000, currentCooldown: 0,
                damage: 0, healAmount: 0, effectType: 'bear_form',
                effectDuration: 8000,
                iconKey: 'spell_bear_form', level: 1, maxLevel: 10,
            },
        ],
        necromancer: [
            {
                id: 'death_coil', name: 'Death Coil',
                description: 'Hurl a bolt of shadow at the closest enemy — deals damage and heals Morveth for 30% of the damage dealt.\nApplies a plague DoT and a 30% slow to the target. The rot spreads to enemies behind the target at 40% damage.\nTalent rank 6: spread becomes a 200px radius around the target (hits enemies on both sides).',
                manaCost: 18, cooldown: 9000, currentCooldown: 0,
                damage: 100, healAmount: 0, effectType: 'death_coil',
                dotFraction: 0.25, effectDuration: 5000,
                iconKey: 'spell_death_coil', level: 1, maxLevel: 10,
            },
            {
                id: 'raise_dead', name: 'Raise Dead',
                description: 'Raise the last enemy that died near Morveth as a skeleton minion.\nMinion fights until slain (45s lifetime) with reduced stats.',
                manaCost: 25, cooldown: 10000, currentCooldown: 0,
                damage: 0, healAmount: 0, effectType: 'raise_dead',
                effectDuration: 0,
                iconKey: 'spell_raise_dead', level: 1, maxLevel: 10,
            },
            {
                id: 'soul_leech', name: 'Soul Leech',
                description: 'Passive: regenerates HP every second equal to 0.02 % max HP per soul (requires souls > 0). Each rank adds +30 % regen.',
                manaCost: 0, cooldown: 0, currentCooldown: 0,
                damage: 0, healAmount: 8, effectType: 'soul_leech',
                iconKey: 'spell_soul_leech', level: 1, maxLevel: 10,
            },
            {
                id: 'bone_shield', name: 'Bone Shield',
                description: 'Conjure a bone barrier that absorbs (30 + SP + 2 × souls) damage. Shield strength scales with soul count.\nThe cast releases a bone-wave knockback that pushes nearby enemies back.\nTalent rank 1: the bone wave also deals (40 + 1×SP) damage on knockback.',
                manaCost: 20, cooldown: 20000, currentCooldown: 0,
                damage: 0, healAmount: 0, effectType: 'bone_shield',
                iconKey: 'spell_bone_shield', level: 1, maxLevel: 10,
            },
        ],
        // Shaman (Zhorak the Stormsinger) — totem-based zone-control caster. Three totems
        // spawn at the midpoint between Zhorak and his nearest enemy; only one totem of
        // each school can exist at a time (recasting destroys the prior). Ancestral
        // Guidance empowers all standing totems instead of adding a direct combat effect.
        shaman: [
            {
                id: 'shaman_totems', name: 'Totems',
                description: 'Plant a totem — rotates Lightning → Healing → Earthbind with each cast, skipping any school already up. Up to three totems can stand on the board at once; the cast refunds if all three are active.\nLightning: zaps nearest enemy for 40 + 0.5×SP / 1.2 s.\nHealing: heals most-wounded ally for 22 + 0.5×SP / 2 s.\nEarthbind: slows enemies in 180 px by 35 %.\nAll last 10 s. Talent ranks empower each school.',
                manaCost: 6, cooldown: 7000, currentCooldown: 0,
                damage: 40, healAmount: 22, effectType: 'shaman_totems',
                effectDuration: 10000,
                iconKey: 'spell_shaman_totems', level: 1, maxLevel: 7,
            },
            {
                id: 'shamanic_buff', name: 'Shamanic Call',
                description: 'Rouse the ancestors — grants the party a random shamanic blessing for 3 s:\n• 20 % chance on hit: strike again\n• 20 % chance on hit: bonus fire damage\n• 10 % chance on hit: slow the target 50 % / 1 s\nTalent ranks extend duration (Rank 5: 8 s).',
                manaCost: 18, cooldown: 12000, currentCooldown: 0,
                damage: 0, healAmount: 0, effectType: 'shamanic_buff',
                effectDuration: 3000,
                iconKey: 'spell_shamanic_call', level: 1, maxLevel: 5,
            },
            {
                id: 'shaman_shield', name: 'Spirit Shield',
                description: 'Envelop Zhorak in a shamanic shield — alternates Mana Shield / Lightning Shield each cast.\nMana Shield: absorbs 60 + 2×SP damage across 3 stacks; each absorbed hit restores mana to Zhorak.\nLightning Shield: absorbs 60 + 2×SP damage across 3 stacks; each absorbed hit reflects 5 % of damage back to the attacker.\nOnly one shield may be up at a time.',
                manaCost: 20, cooldown: 10000, currentCooldown: 0,
                damage: 0, healAmount: 0, effectType: 'shaman_shield',
                effectDuration: 20000,
                iconKey: 'spell_spirit_shield', level: 1, maxLevel: 6,
            },
            {
                id: 'ancestral_guidance', name: 'Ancestral Guidance',
                description: 'Call the ancestors — Zhorak\'s standing totem ticks 2× as fast and +50 % stronger for 8 s, and has its remaining duration refreshed to full.\nTalent ranks scale potency doubling. Rank 5 adds +2 s buff duration.',
                manaCost: 35, cooldown: 50000, currentCooldown: 0,
                damage: 0, healAmount: 0, effectType: 'ancestral_guidance',
                effectDuration: 8000,
                iconKey: 'spell_ancestral_guidance', level: 1, maxLevel: 5,
            },
        ],
        // Cyrith the Stormbound (sorcerer) — battlemage hybrid: 2H sword + offhand
        // staff. Summons a Copper Golem to anchor the front while she skirmishes
        // mid-grid, raining lightning. ATK + SP both scale her kit so neither
        // stat alone is optimal. Cooldowns are intentionally long (sister of the
        // wizard rotation, not its replacement).
        sorcerer: [
            {
                id: 'summon_copper_golem', name: 'Forge Vorrak',
                description: 'Calls forth Vorrak — a storm-tempered sentinel who holds the line.\n\n• A sturdy frontline guardian, harder to break with each talent rank.\n• Swings rhythmically; every fourth strike is a heavy chain-slam with a high crit chance.\n• His damage rises with Cyrith\'s strength and arcane power.\n• No cooldown while Vorrak still stands — recasting refunds the mana. The forge cools only when he falls.',
                manaCost: 60, cooldown: 40000, currentCooldown: 0,
                damage: 0, healAmount: 0, effectType: 'summon_copper_golem',
                iconKey: 'spell_summon_golem', level: 1, maxLevel: 4,
            },
            {
                id: 'storm_bolt', name: 'Storm Bolt',
                description: 'Looses a kite of crackling storm at the nearest foe, dealing heavy lightning damage that scales with arcane power. Stuns the target for 0.6s and chains to the nearest enemy within 150px for 60% damage.\nTalent rank 6 (Chain Storm): chains to a second enemy and stun extends to 1.2s.',
                manaCost: 25, cooldown: 14000, currentCooldown: 0,
                damage: 35, healAmount: 0, effectType: 'storm_bolt',
                iconKey: 'spell_storm_bolt', level: 1, maxLevel: 10,
            },
            {
                id: 'static_field', name: 'Static Field',
                description: 'Wraps Vorrak in a humming lightning shield for a few seconds — every foe close enough (110px) is shocked and slowed 25% each second.\nRequires Vorrak to be alive; the field follows him as he moves.\nTalent rank 4 (Greater Field): radius widens to 150px and slow climbs to 40%.',
                manaCost: 40, cooldown: 32000, currentCooldown: 0,
                damage: 20, healAmount: 0, effectType: 'static_field',
                effectDuration: 4000,
                iconKey: 'spell_static_field', level: 1, maxLevel: 4,
            },
            {
                id: 'arc_strike', name: 'Arc Strike',
                description: 'Leaps onto the nearest foe and brings down a blade wreathed in lightning, scaling with both her strike and arcane power. The blow arcs onto a nearby enemy within 160px for 65% damage.\nOnly triggers from a front-rank slot. Talent rank 4 unlocks a second chain target.',
                manaCost: 30, cooldown: 26000, currentCooldown: 0,
                damage: 50, healAmount: 0, effectType: 'arc_strike',
                iconKey: 'spell_arc_strike', level: 1, maxLevel: 6,
            },
        ],
    };
    return abilities[heroKey] ?? abilities['warrior'];
}
//# sourceMappingURL=progression.js.map